import logoVariants from '@/assets/img/logo';
import iconsPng from '@/assets/img/png/icons';
import LoadingPage from '@/components/templates/LoadingPage';
import authRoutes from '@/constants/routes/auth';
import useToast from '@/hooks/useToast';
import onboardingMessages from '@/messages/onboarding';
import { Image, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import roles from '../../constants/roles.ts';
import { CLIENT_APP_SIGN_IN_URL } from '@/constants/stringVars.ts';
import BlueTopPage from '@/components/templates/BlueTopPage/index.tsx';
import NewPasswordForm from './NewPasswordForm.tsx';
import { getAccountStateReason, getMaintenanceEndLabel } from '@/services/api/accountState';
import type { PasswordSetupFailureReason } from '@/types/generated/schemas';

const setupFailureReasons: Record<PasswordSetupFailureReason, string> = {
  LINK_INVALID: 'This password setup link is invalid. Please ask your administrator for a new invitation.',
  LINK_EXPIRED: 'This invitation has expired. Please ask your administrator for a new one.',
  SETUP_IN_PROGRESS: 'Password setup is in progress. Please try again later.',
  ORGANIZATION_INACTIVE:
    'This invitation cannot be used right now because your organization is inactive. Please contact your administrator.',
  PASSWORD_REJECTED: 'This password does not meet the password policy. Please choose another one.',
};

const getSetupFailureReason = (value: unknown): PasswordSetupFailureReason | undefined => {
  if (!value || typeof value !== 'object' || !('reason' in value)) return undefined;
  const reason = value.reason;
  return typeof reason === 'string' && Object.hasOwn(setupFailureReasons, reason)
    ? (reason as PasswordSetupFailureReason)
    : undefined;
};

const firstErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('errors' in error) || !Array.isArray(error.errors)) return undefined;
  const first = error.errors[0];
  if (typeof first === 'string') return first;
  return first && typeof first === 'object' && 'message' in first && typeof first.message === 'string'
    ? first.message
    : undefined;
};

const hasUnrecognizedReason = (value: unknown): boolean =>
  !!value &&
  typeof value === 'object' &&
  'reason' in value &&
  typeof value.reason === 'string' &&
  !Object.hasOwn(setupFailureReasons, value.reason);

const setupRefusalMessage = (error: unknown, passwordSubmission = false): string | undefined => {
  const status = error && typeof error === 'object' && 'code' in error ? error.code : undefined;
  if (status === 429) {
    return passwordSubmission ? 'Please wait a minute and try again.' : 'Please wait a minute and reload this page.';
  }

  const reason = getSetupFailureReason(error);
  if (reason) {
    return reason === 'PASSWORD_REJECTED'
      ? (firstErrorMessage(error) ?? setupFailureReasons[reason])
      : setupFailureReasons[reason];
  }

  if (hasUnrecognizedReason(error) && !getAccountStateReason(error)) return firstErrorMessage(error);

  if (status === 503 || status === 0) {
    return passwordSubmission
      ? 'We could not confirm whether your password was set. Try signing in with it before submitting again.'
      : 'We could not verify this link right now. Please try again later.';
  }

  switch (getAccountStateReason(error)) {
    case 'ACCOUNT_OPERATION_IN_PROGRESS':
      return 'Another account operation is in progress. Please try again in a moment.';
    case 'MAINTENANCE_FREEZE': {
      const end = getMaintenanceEndLabel(error);
      return `Invitations can be accepted again after planned maintenance${end ? `, planned to end ${end}` : ''}.`;
    }
    default:
      return undefined;
  }
};

const isLinkFailure = (reason: PasswordSetupFailureReason | undefined): boolean =>
  reason === 'LINK_EXPIRED' ||
  reason === 'LINK_INVALID' ||
  reason === 'ORGANIZATION_INACTIVE' ||
  reason === 'SETUP_IN_PROGRESS';

interface Props {
  pageTitle: string;
  pageDescription: string;
  email: string;
  authToken: string;
  isValidLink: boolean;
  verifyTokenCallback: () => Promise<{ success: boolean; reason?: PasswordSetupFailureReason }>;
  setPasswordCallback: (password: string) => Promise<{ success: boolean }>;
  userRoles: string[];
}

const UserPasswordPage = ({
  pageTitle,
  pageDescription,
  email,
  authToken,
  isValidLink,
  verifyTokenCallback,
  setPasswordCallback,
  userRoles,
}: Props) => {
  const [initIsLoading, setInitIsLoading] = useState(isValidLink);
  const [verificationSuccessful, setVerificationSuccessful] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [linkFailureReason, setLinkFailureReason] = useState<PasswordSetupFailureReason>();

  const [isPasswordSubmitLoading, setIsPasswordSubmitLoading] = useState(false);
  const [passwordSubmitSuccessful, setPasswordSubmitSuccessful] = useState(true); // initially true so that we can set it to false if something goes wrong
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

  const navigate = useNavigate();
  const toast = useToast();

  const verifyAuthTokenWithEmail = useCallback(async () => {
    if (!authToken || !isValidLink) {
      setInitIsLoading(false);
      setVerificationSuccessful(false);

      return;
    }

    try {
      const result = await verifyTokenCallback();
      const { success } = result;
      const reason = getSetupFailureReason(result);
      setVerificationSuccessful(success);
      if (!success) {
        setLinkFailureReason(isLinkFailure(reason) ? reason : undefined);
        setVerificationMessage(setupRefusalMessage(result) ?? '');
      }
    } catch (error) {
      setVerificationSuccessful(false);
      const reason = getSetupFailureReason(error);
      setLinkFailureReason(isLinkFailure(reason) ? reason : undefined);
      setVerificationMessage(setupRefusalMessage(error) ?? '');
    } finally {
      setInitIsLoading(false);
    }
  }, [authToken, email, isValidLink]);

  useEffect(() => {
    verifyAuthTokenWithEmail();
  }, []);

  const onSubmitPasswordForm = async (password: string) => {
    setIsPasswordSubmitLoading(true);
    setPasswordErrorMessage('');

    let success = false;

    try {
      ({ success } = await setPasswordCallback(password));
    } catch (error) {
      // A rejection does not prove the password was not set. In particular, a
      // 503 can arrive after the server accepted the write.
      success = false;
      const reason = getSetupFailureReason(error);
      if (isLinkFailure(reason)) {
        setLinkFailureReason(reason);
        setVerificationMessage(setupRefusalMessage(error) ?? '');
        setVerificationSuccessful(false);
      }
      setPasswordErrorMessage(setupRefusalMessage(error, true) ?? '');
    }

    if (success) {
      toast({
        status: 'success',
        title: 'Success',
        description: 'Password change successful!',
        position: 'top-right',
      });

      if (userRoles.includes(roles.orgEmployee) || userRoles.includes(roles.orgAdmin)) {
        window.location.replace(CLIENT_APP_SIGN_IN_URL);
      } else {
        navigate({ to: '/sign-in' });
      }
    } else {
      setPasswordSubmitSuccessful(false);
    }

    setIsPasswordSubmitLoading(false);
  };

  if (initIsLoading) return <LoadingPage />;

  if (!verificationSuccessful) {
    return (
      <VStack paddingTop={'50px'}>
        <Image
          src={logoVariants.purple252x80}
          alt="my-omnia"
          _hover={{ cursor: 'pointer' }}
          width={'126px'}
          height={'40px'}
          onClick={() => {
            navigate({ to: authRoutes.signIn });
          }}
        />
        <VStack paddingTop={'30px'}>
          <Image
            alt="edit-icon"
            src={iconsPng.orangeExclamationMark}
            width={'120px'}
            height={'120px'}
            objectFit="cover"
            style={{ margin: '40px 0' }}
          />
          <Text textAlign={'center'} variant="loraTitle" color="text.mediumBlue" fontSize={'26px'}>
            {linkFailureReason === 'LINK_EXPIRED'
              ? 'Invitation expired'
              : linkFailureReason === 'LINK_INVALID'
                ? 'Invalid invitation link'
                : verificationMessage
                  ? 'Unable to continue password setup'
                  : onboardingMessages.emailNotVerifiedSuccessfully}
          </Text>
          <Text
            textAlign={'center'}
            color={'text.mediumBlue'}
            opacity={0.7}
            lineHeight={'20px'}
            fontWeight={'600'}
            paddingTop={'20px'}
          >
            {verificationMessage || onboardingMessages.checkEmailOrContactAdmin}
          </Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <BlueTopPage title={pageTitle} description={pageDescription} hideButtons centeredLogo>
      <VStack>
        {verificationSuccessful && (
          <NewPasswordForm onSubmit={onSubmitPasswordForm} isLoading={isPasswordSubmitLoading} />
        )}

        {!passwordSubmitSuccessful && (
          <Text variant={'error'}>{passwordErrorMessage || onboardingMessages.passwordSetupUnsuccessful}</Text>
        )}
      </VStack>
    </BlueTopPage>
  );
};

export default UserPasswordPage;
